import { useVirtualizer } from "@tanstack/react-virtual";
import { Field, FieldArray, Form, Formik } from "formik";
import React, { useCallback, useMemo, useState } from "react";
import { FaTimesCircle } from "react-icons/fa";
import ReactModal from "react-modal";
import { useParams } from "react-router";
import * as Yup from "yup";
import { BorderlessButton } from "../../components/BorderlessButton";
import { Loading } from "../../components/Loading";
import { ModalHeader } from "../../components/ModalHeader";
import { FormField } from "../../forms/FormField";
import { useAccount, useAssignUser, useShowSettings } from "../account-api";
import styles from "./UserModal.module.scss";

export const validationSchema = Yup.object().shape({
    name: Yup.string().label("Name").max(100).required(),
    email: Yup.string().email().label("Email").max(256).required()
        .when(["accountPermission", "allowedEmailDomainPattern"],
            ([accountPermission, allowedEmailDomainPattern], schema) => {
            return accountPermission == "Admin" && allowedEmailDomainPattern
                ? schema.matches(new RegExp(allowedEmailDomainPattern), `Admin user emails must include: ${allowedEmailDomainPattern}`)
                : schema;
        })
});

export function UserModal({ user, close }) {
    const { accountID } = useParams();
    const { data: account } = useAccount(accountID);
    const assignUser = useAssignUser(accountID);

    if (!account) return;

    const initialValues = user
        ? {
              ...user,
            accountPermission: user.accountPermission ?? "",
            allowedEmailDomainPattern: account.allowedEmailDomainPattern
          }
        : {
            allowedEmailDomainPattern: account.allowedEmailDomainPattern
        };

    return (
        <ReactModal isOpen={!!user} onRequestClose={close}>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={values => assignUser.mutateAsync(values).then(close)}
            >
                <Form className={styles["modal-form"]}>
                    <header>
                        <ModalHeader close={close}>Edit User Access</ModalHeader>
                    </header>
                    <main style={{ display: "flex", flexDirection: "column" }}>
                        <fieldset className="form">
                            <FormField
                                name="name"
                                label="Name"
                                style={{ width: "40ch" }}
                                disabled={!!user?.userID}
                            />
                            <FormField
                                name="email"
                                label="Email"
                                style={{ width: "40ch" }}
                                disabled={!!user?.userID}
                                help={user?.userID ? null : "User will receive an email invitation with a link to join your account"}
                            />
                            <FormField
                                name="accountPermission"
                                label="Access"
                                component={AccessRadioButtons}
                            />
                        </fieldset>
                        <FieldArray name="shows" component={ShowSelector} />
                    </main>
                    <footer>
                        <button type="button" onClick={close}>
                            Cancel
                        </button>
                        <button type="submit">{user?.userID ? "Save" : "Send Invitation"}</button>
                    </footer>
                </Form>
            </Formik>
        </ReactModal>
    );
}

function AccessRadioButtons({ field }) {
    return (
        <div style={{ display: "flex", flexDirection: "row", gap: "1em" }}>
            <label>
                <Field type="radio" name={field.name} value="Admin" />
                <span>
                    <b>Admin access</b> to all shows in this account
                </span>
            </label>
            <label>
                <Field type="radio" name={field.name} value="Viewer" />
                <span>
                    <b>Viewer access</b> to all shows in this account
                </span>
            </label>
            <label>
                <Field type="radio" name={field.name} value="" />
                <span>
                    Access to <b>only the shows selected below</b>
                </span>
            </label>
        </div>
    );
}

function ShowSelector({ push, remove, form, name }) {
    const { accountID } = useParams();
    const { data: available } = useShowSettings(accountID);
    const [filter, setFilter] = useState("");

    const shows = form.values[name];

    //const filtered = available?.filter(a => !shows?.find(s => s.showID == a.showID));

    const filtered = useMemo(() => {
        const words = filter.split(" ").filter(Boolean);
        const res = words.map(w => new RegExp(w, "i"));
        return available
            ?.filter(a => !shows?.find(s => s.showID == a.showID))
            .filter(s => res.reduce((isMatch, re) => isMatch && re.test(s.title), true));
    }, [available, shows, filter]);

    const handleShowSelected = useCallback(
        show => {
            push(show);
            form.setFieldValue("accountPermission", "");
        },
        [push, form]
    );

    const filterChanged = event => setFilter(event.target.value);

    return (
        <div
            style={{
                display: "grid",
                flex: "1 1 auto",
                minHeight: "15em",
                gridTemplateColumns: "50% 50%",
                gridTemplateRows: "min-content 1fr",
                background: "#fafafa"
            }}
        >
            <div
                style={{
                    display: "flex",
                    gap: "0.5em",
                    alignItems: "center",
                    padding: "0.25em",
                    background: "#eee",
                    border: "1px solid #ddd"
                }}
            >
                <b>Shows</b>
                {filtered ? <span>({filtered.length})</span> : null}
                <input
                    placeholder="find by title"
                    style={{ flex: 1 }}
                    value={filter}
                    onChange={filterChanged}
                />
                <BorderlessButton
                    onClick={() => setFilter("")}
                    style={{ visibility: filter ? "visible" : "hidden" }}
                >
                    ×
                </BorderlessButton>
            </div>

            <div
                style={{
                    display: "flex",
                    gap: "1em",
                    alignItems: "center",
                    padding: "0.25em",
                    background: "#eee",
                    border: "1px solid #ddd"
                }}
            >
                <b>Selected for this user</b>
            </div>

            {available ? (
                <AvailableShows filtered={filtered} onShowSelected={handleShowSelected} />
            ) : (
                <Loading />
            )}

            <div
                style={{
                    flex: "1 1 0px",
                    overflowY: "scroll",
                    border: "1px solid #ddd"
                }}
            >
                {shows?.map((s, i) => (
                    <div
                        key={s.showID}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            margin: "0.25em",
                            gap: "0.25em"
                        }}
                    >
                        <img src={s.podcastImageUrl} style={{ width: 32, height: 32 }} />
                        <span style={{ flex: 1 }}>{s.title}</span>
                        <Field as="select" name={`${name}[${i}].showPermission`}>
                            <option>Viewer</option>
                            <option>Admin</option>
                        </Field>
                        {/*<Field name={`${ name }[${ i }].showPermission`} component={AccessSelectField} />*/}
                        <BorderlessButton onClick={() => remove(i)}>
                            <FaTimesCircle />
                        </BorderlessButton>
                    </div>
                ))}
            </div>
        </div>
    );
}

const AvailableShows = React.memo(({ filtered, onShowSelected }) => {
    const parentRef = React.useRef();

    const rowVirtualizer = useVirtualizer({
        count: filtered?.length,
        getScrollElement: () => parentRef.current,
        getItemKey: index => filtered[index].showID,
        estimateSize: () => 40
    });

    return (
        <div
            ref={parentRef}
            style={{ flex: "1 1 0px", overflowY: "scroll", border: "1px solid #ddd" }}
        >
            <div
                style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    width: "100%",
                    position: "relative"
                }}
            >
                {/*filtered?.map((s, i) => <div key={s.showID} style={{ display: "flex", alignItems: "center", margin: "0.25em", gap: "0.25em" }}>
                <img src={s.podcastImageUrl} style={{ width: 32, height: 32 }} />
                <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.title}</span>
                <AccessSelect onChange={value => {
                    if (value) {
                        push({
                            showID: s.showID,
                            title: s.title,
                            showPermission: value,
                            podcastImageUrl: s.podcastImageUrl
                        });
                    }
                }} />
            </div>)*/}
                {rowVirtualizer.getVirtualItems().map(virtualItem => {
                    const s = filtered[virtualItem.index];
                    return (
                        <div
                            key={virtualItem.key}
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: `${virtualItem.size}px`,
                                transform: `translateY(${virtualItem.start}px)`,
                                display: "flex",
                                alignItems: "center",
                                padding: "0.25em",
                                gap: "0.25em"
                            }}
                        >
                            <img src={s.podcastImageUrl} style={{ width: 32, height: 32 }} />
                            <span
                                style={{
                                    flex: 1,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis"
                                }}
                            >
                                {s.title}
                            </span>
                            <select
                                onChange={event => {
                                    if (event.target.value) {
                                        onShowSelected({
                                            showID: s.showID,
                                            title: s.title,
                                            showPermission: event.target.value,
                                            podcastImageUrl: s.podcastImageUrl
                                        });
                                    }
                                }}
                            >
                                <option value="">Select…</option>
                                <option>Admin</option>
                                <option>Viewer</option>
                            </select>
                            {/*<AccessSelect onChange={value => {*/}
                            {/*    if (value) {*/}
                            {/*        push({*/}
                            {/*            showID: s.showID,*/}
                            {/*            title: s.title,*/}
                            {/*            showPermission: value,*/}
                            {/*            podcastImageUrl: s.podcastImageUrl*/}
                            {/*        });*/}
                            {/*    }*/}
                            {/*}} />*/}
                        </div>
                    );
                })}
            </div>
        </div>
    );
});
